// In-editor previews for each subtype. These render a rough impression of how the subtype
// looks in the employee app; they read from `state.properties`. (The incentivation-page
// detail/summary views are a separate, not-yet-migrated concern.)
import { Check, ImagePlus, Pencil, Plus, Star, ThumbsDown, ThumbsUp, X } from 'lucide-react'
import type { TFunction } from 'i18next'
import { Input } from '@/components/shadcn/input'
import { Button } from '@/components/shadcn/button'
import type { SubtypeState } from '@/lib/action-modules/registry/types'
import type { ChecklistAnswerItem, ChecklistAnswerType } from '@/lib/action-modules/registry/subtypes/checklist'

export function TextAnswerPreview(state: SubtypeState) {
  const hintText = state.properties.hintText as string | undefined
  return <Input disabled placeholder={hintText ?? ''} />
}

export function ChecklistAnswerPreview({ properties }: SubtypeState) {
  const items = (properties.checklistItems ?? []) as ChecklistAnswerItem[]
  const answerType = properties.checklistAnswerType as ChecklistAnswerType

  return (
    <div className="flex flex-col gap-1">
      {items.map((item, i) => {
        if (answerType === 'MultipleChoiceWithDecline') {
          return (
            <div key={item.id ?? i} className="flex items-center gap-2">
              <span className="flex-1 text-sm">{item.text}</span>
              <X className="size-4 text-muted-foreground" />
              <Check className="size-4 text-muted-foreground" />
            </div>
          )
        }
        const shape = answerType === 'MultipleChoice' ? 'rounded-sm' : 'rounded-full'
        return (
          <div key={item.id ?? i} className="flex items-center gap-2 text-sm">
            <span className={`size-4 border ${shape} ${i === 0 ? 'bg-primary' : ''}`} />
            {item.text}
          </div>
        )
      })}
    </div>
  )
}

export function DatePreview() {
  return <Input disabled type="date" />
}

export function DropdownAnswerPreview({ properties }: SubtypeState) {
  const items = (properties.checklistItems ?? []) as ChecklistAnswerItem[]
  return (
    // pointer-events-none so clicks fall through to the row body, which handles selection and
    // drag — a bare disabled <select> would otherwise swallow them (unlike the shadcn Input/Button
    // previews, whose disabled: styles already include pointer-events-none).
    <select disabled className="pointer-events-none h-9 w-full rounded-md border bg-transparent px-3 text-sm">
      {items.map((item, i) => (
        <option key={item.id ?? i}>{item.text}</option>
      ))}
    </select>
  )
}

export function LinkPreview(state: SubtypeState, t: TFunction) {
  const buttonName = state.properties.buttonName as string | undefined
  return (
    <Button type="button" variant="outline" disabled>
      {buttonName || t('subtypes.link.previewButtonName')}
    </Button>
  )
}

function PictureUploadBox({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-2xl border border-muted-foreground/40 p-6 text-sm">
      <ImagePlus className="size-5" /> {label}
    </div>
  )
}

export function PictureUploadPreview(state: SubtypeState, t: TFunction) {
  const description = state.properties.pictureDescription as string | undefined
  const exampleImageUrl = state.properties.exampleImageUrl as string | undefined
  return (
    <div className="flex flex-col gap-2">
      {description && <p className="text-sm">{description}</p>}
      {exampleImageUrl && <img src={exampleImageUrl} alt="" className="w-full" />}
      <PictureUploadBox label={t('subtypes.pictureUpload.previewTakePicture')} />
    </div>
  )
}

export function MultiPictureUploadPreview(state: SubtypeState, t: TFunction) {
  const description = state.properties.pictureDescription as string | undefined
  const exampleImageUrl = state.properties.exampleImageUrl as string | undefined
  return (
    <div className="flex flex-col gap-2">
      {description && <p className="text-sm">{description}</p>}
      {exampleImageUrl && <img src={exampleImageUrl} alt="" className="w-full" />}
      <PictureUploadBox label={t('subtypes.multiPictureUpload.previewTakePicture')} />
    </div>
  )
}

function VoucherBox({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-2xl border border-muted-foreground/40 p-6 text-sm">
      <Plus className="size-5" /> {label}
    </div>
  )
}

export const AnniversaryBonusPreview = (_state: SubtypeState, t: TFunction) => (
  <VoucherBox label={t('subtypes.anniversaryBonus.previewSelectVoucher')} />
)
export const BirthdayBonusPreview = (_state: SubtypeState, t: TFunction) => (
  <VoucherBox label={t('subtypes.birthdayBonus.previewSelectVoucher')} />
)

export const EvaluationStarsPreview = () => (
  <div className="flex justify-center gap-1">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className="size-6" />
    ))}
  </div>
)

export const EvaluationThumbsPreview = () => (
  <div className="flex justify-center gap-3">
    <ThumbsDown className="size-6" />
    <ThumbsUp className="size-6" />
  </div>
)

// Mirrors the mobile dial in its initial (not yet started) state: a blue circle with the
// elapsed time and the start label, plus the edit action below it.
export const StopwatchPreview = (state: SubtypeState, t: TFunction) => (
  <div className="flex flex-col items-center gap-2 pt-2">
    <div className="flex size-40 flex-col items-center justify-center gap-2 rounded-full bg-blue-600 text-white shadow-lg shadow-blue-600/35">
      <span className="text-2xl font-bold tabular-nums">00:00:00</span>
      <span className="text-sm font-semibold">{t('subtypes.stopwatch.previewStart')}</span>
    </div>
    {state.properties.allowManualDuration === true && (
      <span className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary">
        <Pencil className="size-4" /> {t('subtypes.stopwatch.previewEdit')}
      </span>
    )}
  </div>
)

export const NoPreview = (_state: SubtypeState, t: TFunction) => (
  <p className="text-sm text-muted-foreground">{t('subtypes.base.noPreview')}</p>
)
